import { Dispatch } from 'react-redux';

import { Action } from '../../actions';
import { StoreState } from '../../reducers';
import ApiUtils from '../../utils/api';
import RoutesUtils from '../../utils/routes';
import { Api } from '../../typings/api';
import Utils from '../../utils';
import { ProposalSource, ProposalCreationProgress, ProposalCreationStatus, SelectedTemplate } from './reducer';
import { createNotification } from '../NotificationBar/actions';
import { getOrFetchDriveItemMeta } from '../DriveItemList/actions';
import { FileError } from '../ImportManager/actions';
import { openNoSaveAsPopup } from '../NoSaveAsPopup/actions';
import { receiveDriveItems, receiveResolutions, addCustomTemplate, removeCustomTemplate } from '../DriveItemList/actions';

export enum ProposalCreatorActionKeys {
	GET_PROPOSAL_TEMPLATES = '@@proposalCreator/GET_PROPOSAL_TEMPLATES',
	SET_PROPOSAL_TEMPLATES = '@@proposalCreator/SET_PROPOSAL_TEMPLATES',
	SHOW = '@@proposalCreator/SHOW',
	HIDE = '@@proposalCreator/HIDE',
	SELECT_TEMPLATE = '@@proposalCreator/SELECT_TEMPLATE',
	SET_NAME = '@@proposalCreator/SET_NAME',
	SET_ERROR = '@@proposalCreator/SET_ERROR',
	SET_REQUEST_ID = '@@proposalCreator/SET_REQUEST_ID',
	SET_CREATION_STATUS = '@@proposalCreator/SET_CREATION_STATUS',
	UPDATE_CREATION_PROGRESS = '@@proposalCreator/UPDATE_CREATION_PROGRESS',
	REMOVE_FROM_CREATION_PROGRESS = '@@proposalCreator/REMOVE_FROM_CREATION_PROGRESS',
	SET_AS_TEMPLATE = '@@proposalCreator/SET_AS_TEMPLATE',
	REMOVE_TEMPLATE_FROM_STORE = '@@proposalCreator/REMOVE_TEMPLATE_FROM_STORE',
}

interface GetProposalTemplatesAction extends Action {
	type: ProposalCreatorActionKeys.GET_PROPOSAL_TEMPLATES;
}

interface SetProposalTemplatesAction extends Action {
	type: ProposalCreatorActionKeys.SET_PROPOSAL_TEMPLATES;
	masterProposals: Api.DriveItem[];
	customTemplates: Api.DriveItem[];
}

interface ShowProposalCreatorAction extends Action {
	type: ProposalCreatorActionKeys.SHOW;
	source: ProposalSource;
}

interface HideProposalCreatorAction extends Action {
	type: ProposalCreatorActionKeys.HIDE;
}

interface SelectTemplateAction extends Action {
	type: ProposalCreatorActionKeys.SELECT_TEMPLATE;
	data: SelectedTemplate;
}

interface SetNameAction extends Action {
	type: ProposalCreatorActionKeys.SET_NAME;
	name: string;
}

interface SetErrorAction extends Action {
	type: ProposalCreatorActionKeys.SET_ERROR;
	error: string | null;
}

interface SetRequestIdAction extends Action {
	type: ProposalCreatorActionKeys.SET_REQUEST_ID;
	requestId: string;
}

interface SetCreationStatusAction extends Action {
	type: ProposalCreatorActionKeys.SET_CREATION_STATUS;
	status: ProposalCreationStatus;
}

interface UpdateCreationProgressAction extends Action {
	type: ProposalCreatorActionKeys.UPDATE_CREATION_PROGRESS;
	data: ProposalCreationProgress;
}

interface RemoveFromCreationProgressAction extends Action {
	type: ProposalCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS;
}

interface RemoveTemplateFromStoreAction extends Action {
	type: ProposalCreatorActionKeys.REMOVE_TEMPLATE_FROM_STORE;
	id: number;
}

export type ProposalCreatorAction =
	GetProposalTemplatesAction |
	SetProposalTemplatesAction |
	ShowProposalCreatorAction |
	HideProposalCreatorAction |
	SelectTemplateAction |
	SetNameAction |
	SetErrorAction |
	SetRequestIdAction |
	SetCreationStatusAction |
	UpdateCreationProgressAction |
	RemoveFromCreationProgressAction |
	RemoveTemplateFromStoreAction;

export const setProposalTemplates = (masterProposals: Api.DriveItem[], customTemplates: Api.DriveItem[]): SetProposalTemplatesAction => ({
	type: ProposalCreatorActionKeys.SET_PROPOSAL_TEMPLATES,
	masterProposals,
	customTemplates,
});

export const showProposalCreator = (source: ProposalSource): ShowProposalCreatorAction => ({
	type: ProposalCreatorActionKeys.SHOW,
	source,
});

export const hideProposalCreator = () => (dispatch: Dispatch<StoreState>) => {
	dispatch(removeFromCreationProgress());
	dispatch({
		type: ProposalCreatorActionKeys.HIDE,
	});
};

export const selectTemplate = (data: SelectedTemplate): SelectTemplateAction => ({
	type: ProposalCreatorActionKeys.SELECT_TEMPLATE,
	data,
});

export const setName = (name: string): SetNameAction => ({
	type: ProposalCreatorActionKeys.SET_NAME,
	name,
});

export const setError = (error: string | null): SetErrorAction => ({
	type: ProposalCreatorActionKeys.SET_ERROR,
	error,
});

export const setCreationStatus = (status: ProposalCreationStatus): SetCreationStatusAction => ({
	type: ProposalCreatorActionKeys.SET_CREATION_STATUS,
	status,
});

export const openProposalCreatorForSaveAs = ({ id, label }: Api.DriveItem) => (dispatch: Dispatch<StoreState>) => {
	dispatch(showProposalCreator(ProposalSource.PROPOSAL));
	dispatch(selectTemplate({
		id,
		label,
	}));
};

export const updateCreationProgress = (data: ProposalCreationProgress): UpdateCreationProgressAction => ({
	type: ProposalCreatorActionKeys.UPDATE_CREATION_PROGRESS,
	data,
});

export const removeFromCreationProgress = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState) => {
	const {
		proposalCreator: { requestId },
	} = getState();

	dispatch({
		type: ProposalCreatorActionKeys.REMOVE_FROM_CREATION_PROGRESS,
	});

	if (requestId) {
		Utils.cancelRequest(requestId);
	}
};

export const getProposalTemplates = () => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		dispatch({
			type: ProposalCreatorActionKeys.GET_PROPOSAL_TEMPLATES,
		});

		const {
			masterProposals,
			customTemplates,
			resolutions,
		} = await ApiUtils.getProposalTemplates();

		dispatch(receiveDriveItems(Utils.itemsToById(masterProposals)));
		dispatch(receiveDriveItems(Utils.itemsToById(customTemplates)));
		dispatch(receiveResolutions(resolutions));
		dispatch(setProposalTemplates(masterProposals, customTemplates));
	} catch (error) {
		dispatch(hideProposalCreator());
		dispatch(createNotification('error.fetch_proposal_templates_error'));
	}
};

export const createProposalFromMasterProposal = (publicationGId: number, label: string) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const {
			galleryInfos: { config: { clientId, applicationGallery } },
		} = getState();

		const folderId = Utils.getFolderIdForPrivateResource();

		if (!folderId) {
			return dispatch(createNotification('error.create_proposal_error'));
		}

		const { resource: { globalId } } = await ApiUtils.createProposalFromMasterProposal(clientId, applicationGallery, folderId, publicationGId, label);
		dispatch(hideProposalCreator());

		RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, globalId));

		await dispatch(getOrFetchDriveItemMeta(folderId, true, true, true));
	} catch (error) {
		if (ApiUtils.isDuplicateFileNameError(error.responseText)) {
			dispatch(setError('error.duplicate_file_name'));
		} else if (error.responseText.includes('PROP_SAVE_FORBIDDEN')) {
			dispatch(hideProposalCreator());
			dispatch(openNoSaveAsPopup());
		} else {
			dispatch(hideProposalCreator());
			dispatch(createNotification('error.create_proposal_error'));
		}
	}
};

const getProposalCreationProgress = (
	folderId: number,
	clientId: number,
	globalId: number,
	status: Api.AssetItemStatus,
) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		const onProgressUpdate = (percentage: number) => {
			dispatch(updateCreationProgress({
				percentage,
			}));
		};

		const onProgressError = () => {
			dispatch(updateCreationProgress({
				error: FileError.PROCESSING,
				percentage: 100,
			}));
		};

		const onProgressFinish = async () => {
			dispatch(updateCreationProgress({
				percentage: 100,
			}));
			await dispatch(getOrFetchDriveItemMeta(folderId, true, true, true));
			dispatch(hideProposalCreator());
			RoutesUtils.historyPush(RoutesUtils.getPathEditor(clientId, globalId));
		};

		const shouldCancelProgress = (state: StoreState) => {
			return !state.proposalCreator.requestId;
		};

		const onProgressCancel = async () => {
			await ApiUtils.trashResources(clientId, [globalId]);
		};

		if (![
			Api.AssetItemStatus.LIVE,
			Api.AssetItemStatus.ERROR,
		].includes(status)) {
			await ApiUtils.getPublicationProgress(clientId, globalId, onProgressUpdate, onProgressError, onProgressFinish, shouldCancelProgress, onProgressCancel);
		} else {
			if (status === Api.AssetItemStatus.LIVE) {
				onProgressFinish();
			}

			if (status === Api.AssetItemStatus.ERROR) {
				onProgressError();
			}
		}
	} catch (error) {
		dispatch(updateCreationProgress({
			error: FileError.IMPORT,
			percentage: 100,
		}));
	}
};

export const createProposalFromDesktopFile = (
	file: File,
	isCorrectFileType: boolean,
	requestId: string,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		const { proposalCreator: { fromFile } } = getState();
		// to remove previously selected file (e.g., with wrong type)
		if (fromFile.status) {
			dispatch(removeFromCreationProgress());
		}

		dispatch({
			type: ProposalCreatorActionKeys.SET_REQUEST_ID,
			requestId,
		});
		dispatch(setCreationStatus(ProposalCreationStatus.IMPORTING));

		if (!isCorrectFileType) {
			dispatch(updateCreationProgress({
				error: FileError.TYPE,
				percentage: 100,
			}));
			return;
		}

		const {
			galleryInfos: { config: { clientId } },
		} = getState();

		const folderId = Utils.getFolderIdForPrivateResource();

		if (!folderId) {
			dispatch(hideProposalCreator());
			return dispatch(createNotification('error.create_proposal_error'));
		}

		const onImportProgress = (e: ProgressEvent) => {
			const percentage = Math.floor((e.loaded / e.total) * 100);
			dispatch(updateCreationProgress({
				percentage,
			}));
		};

		const { globalId, status } = await ApiUtils.createProposalFromDesktopFile(file, clientId, folderId, onImportProgress, requestId);

		dispatch(setCreationStatus(ProposalCreationStatus.PROCESSING));
		await dispatch(getProposalCreationProgress(folderId, clientId, globalId, status));
	} catch (error) {
		dispatch(updateCreationProgress({
			error: FileError.IMPORT,
			percentage: 100,
		}));
	}
};

export const createProposalFromDriveItem = (
	{ id, label }: Api.DriveItem,
	requestId: string,
) => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	try {
		dispatch({
			type: ProposalCreatorActionKeys.SET_REQUEST_ID,
			requestId,
		});
		dispatch(setCreationStatus(ProposalCreationStatus.IMPORTING));

		const {
			galleryInfos: { config: { clientId } },
		} = getState();

		const folderId = Utils.getFolderIdForPrivateResource();

		if (!folderId) {
			dispatch(hideProposalCreator());
			return dispatch(createNotification('error.create_proposal_error'));
		}

		const { globalId, status } = await ApiUtils.createProposalFromDriveItem(folderId, clientId, id, label, requestId);

		dispatch(setCreationStatus(ProposalCreationStatus.PROCESSING));
		await dispatch(getProposalCreationProgress(folderId, clientId, globalId, status));
	} catch (error) {
		dispatch(updateCreationProgress({
			error: FileError.IMPORT,
			percentage: 100,
		}));
	}
};

export const setAsTemplate = (clientId: number, id: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		await ApiUtils.setAsTemplate(clientId, id);
		dispatch(addCustomTemplate(id));
		dispatch(createNotification('info.set_as_template_success'));
	} catch (error) {
		dispatch(createNotification('error.set_as_template_error'));
	}
};

const removeTemplateFromStore = (id: number): RemoveTemplateFromStoreAction => ({
	type: ProposalCreatorActionKeys.REMOVE_TEMPLATE_FROM_STORE,
	id,
});

export const removeTemplate = (clientId: number, id: number) => async (dispatch: Dispatch<StoreState>): Promise<void> => {
	try {
		await ApiUtils.removeTemplate(clientId, id);
		dispatch(removeCustomTemplate(id));
		dispatch(removeTemplateFromStore(id));
	} catch (error) {
		dispatch(createNotification('error.remove_template_error'));
	}
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/ProposalCreator/actions.ts